import { useEffect } from "react";
import { Flex, Button, Divider } from "antd";

import { useEditor, EditorContent, useEditorState } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { TextStyleKit } from "@tiptap/extension-text-style";

import UndoIcon from "@/icons/UndoIcon";
import RedoIcon from "@/icons/RedoIcon";
import BoldIcon from "@/icons/BoldIcon";
import ItalicIcon from "@/icons/ItalicIcon";
import BulletListIcon from "@/icons/BulletListIcon";
import MinusIcon from "@/icons/MinusIcon";
import NumberListIcon from "@/icons/NumberListIcon";

function MenuBar({ editor }) {
  const editorState = useEditorState({
    editor,
    selector: (ctx) => {
      return {
        isBold: ctx.editor.isActive("bold") ?? false,
        canBold: ctx.editor.can().chain().toggleBold().run() ?? false,
        isItalic: ctx.editor.isActive("italic") ?? false,
        canItalic: ctx.editor.can().chain().toggleItalic().run() ?? false,
        isParagraph: ctx.editor.isActive("paragraph") ?? false,
        isHeading1: ctx.editor.isActive("heading", { level: 1 }) ?? false,
        isHeading2: ctx.editor.isActive("heading", { level: 2 }) ?? false,
        isHeading3: ctx.editor.isActive("heading", { level: 3 }) ?? false,
        isHeading4: ctx.editor.isActive("heading", { level: 4 }) ?? false,
        isHeading5: ctx.editor.isActive("heading", { level: 5 }) ?? false,
        isHeading6: ctx.editor.isActive("heading", { level: 6 }) ?? false,
        isBulletList: ctx.editor.isActive("bulletList") ?? false,
        isOrderedList: ctx.editor.isActive("orderedList") ?? false,
        canUndo: ctx.editor.can().chain().undo().run() ?? false,
        canRedo: ctx.editor.can().chain().redo().run() ?? false,
      };
    },
  });

  return (
    <Flex gap={5} wrap="wrap" align="center">
      <Button
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editorState.canUndo}
        size="small"
      >
        <UndoIcon />
      </Button>
      <Button
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editorState.canRedo}
        size="small"
      >
        <RedoIcon />
      </Button>
      <Divider
        orientation="vertical"
        style={{ borderColor: "var(--color-gray)", marginInline: 5 }}
        size="small"
      />
      <Button
        onClick={() => editor.chain().focus().setParagraph().run()}
        color={editorState.isParagraph ? "primary" : "default"}
        variant={editorState.isParagraph ? "solid" : "outlined"}
        size="small"
      >
        P
      </Button>
      <Button
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        color={editorState.isHeading1 ? "primary" : "default"}
        variant={editorState.isHeading1 ? "solid" : "outlined"}
        size="small"
      >
        H1
      </Button>
      <Button
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        color={editorState.isHeading2 ? "primary" : "default"}
        variant={editorState.isHeading2 ? "solid" : "outlined"}
        size="small"
      >
        H2
      </Button>
      <Button
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        color={editorState.isHeading3 ? "primary" : "default"}
        variant={editorState.isHeading3 ? "solid" : "outlined"}
        size="small"
      >
        H3
      </Button>
      <Button
        onClick={() => editor.chain().focus().toggleHeading({ level: 4 }).run()}
        color={editorState.isHeading4 ? "primary" : "default"}
        variant={editorState.isHeading4 ? "solid" : "outlined"}
        size="small"
      >
        H4
      </Button>
      <Button
        onClick={() => editor.chain().focus().toggleHeading({ level: 5 }).run()}
        color={editorState.isHeading5 ? "primary" : "default"}
        variant={editorState.isHeading5 ? "solid" : "outlined"}
        size="small"
      >
        H5
      </Button>
      <Button
        onClick={() => editor.chain().focus().toggleHeading({ level: 6 }).run()}
        color={editorState.isHeading6 ? "primary" : "default"}
        variant={editorState.isHeading6 ? "solid" : "outlined"}
        size="small"
      >
        H6
      </Button>

      <Button
        onClick={() => editor.chain().focus().toggleBold().run()}
        disabled={!editorState.canBold}
        color={editorState.isBold ? "primary" : "default"}
        variant={editorState.isBold ? "solid" : "outlined"}
        size="small"
      >
        <BoldIcon />
      </Button>
      <Button
        onClick={() => editor.chain().focus().toggleItalic().run()}
        disabled={!editorState.canItalic}
        color={editorState.isItalic ? "primary" : "default"}
        variant={editorState.isItalic ? "solid" : "outlined"}
        size="small"
      >
        <ItalicIcon />
      </Button>
      <Button
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        color={editorState.isBulletList ? "primary" : "default"}
        variant={editorState.isBulletList ? "solid" : "outlined"}
        size="small"
      >
        <BulletListIcon />
      </Button>
      <Button
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        color={editorState.isOrderedList ? "primary" : "default"}
        variant={editorState.isOrderedList ? "solid" : "outlined"}
        size="small"
      >
        <NumberListIcon />
      </Button>

      <Button
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
        size="small"
      >
        <MinusIcon />
      </Button>
    </Flex>
  );
}

const RichText = ({ value = "", onChange, disabled, status }) => {
  const editor = useEditor({
    extensions: [StarterKit, TextStyleKit], // define your extension array
    content: value || "",
    editable: !disabled,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange?.(html);
    },
  });

  useEffect(() => {
    if (!editor) return;
    const next = value || "";
    if (next !== editor.getHTML()) {
      editor.commands.setContent(next || "<p></p>", false);
    }
  }, [value, editor]);

  return (
    <div className="richtext">
      <MenuBar editor={editor} />
      <EditorContent editor={editor} />
    </div>
  );
};

export default RichText;
